import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';

// MSAL Configuration

const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_ENTRA_CLIENT_ID || 'your-client-id',
        authority: process.env.REACT_APP_ENTRA_AUTHORITY || 'https://login.microsoftonline.com/your-tenant-id',
        redirectUri: process.env.REACT_APP_ENTRA_REDIRECT_URI || 'http://localhost:3000',
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
    },
};

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Login request
const loginRequest = {
    scopes: ['User.Read', 'openid', 'profile', 'email'],
};

// Auth Context
const EntraAuthContext = createContext();

// Custom hook to use auth context
export const useEntraAuth = () => {
    const context = useContext(EntraAuthContext);
    if (!context) {
        throw new Error('useEntraAuth must be used within an EntraAuthProvider');
    }
    return context;
};

// Auth Provider Component
export const EntraAuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Inactivity timeout configuration - 10 minutes (600,000 milliseconds)
    const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
    const inactivityTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // Logout function
    const logout = useCallback(async (redirectUri = null) => {
        try {
            // Clear local authentication state first
            setIsAuthenticated(false);
            setUser(null);

            // Clear all local storage and session storage
            localStorage.clear();
            sessionStorage.clear();

            // Get the account to construct logout URL
            const account = msalInstance.getAllAccounts()[0];

            if (account) {
                // Extract tenant ID from authority
                const authority = msalConfig.auth.authority;
                const tenantMatch = authority.match(/\/([^/]+)$/);
                const tenantId = tenantMatch ? tenantMatch[1] : 'common';

                // Build logout URL manually to avoid account picker
                const logoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout`;
                const params = new URLSearchParams();

                // Add post logout redirect URI
                if (redirectUri) {
                    params.append('post_logout_redirect_uri', redirectUri);
                }

                // Redirect to logout URL
                window.location.href = `${logoutUrl}?${params.toString()}`;
            } else {
                // No account, just redirect
                if (redirectUri) {
                    window.location.href = redirectUri;
                }
            }
        } catch (error) {
            console.error('Logout error:', error);
            // Fallback: just redirect
            if (redirectUri) {
                window.location.href = redirectUri;
            }
        }
    }, []);

    // Handle inactivity timeout
    const handleInactivityTimeout = useCallback(() => {
        // Redirect to main website after inactivity logout
        logout('https://www.evoomedical.ro');
    }, [logout]);

    // Reset inactivity timer
    const resetInactivityTimer = useCallback(() => {
        // Clear existing timer
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        // Update last activity timestamp
        lastActivityRef.current = Date.now();

        // Set new timer
        inactivityTimerRef.current = setTimeout(() => {
            handleInactivityTimeout();
        }, INACTIVITY_TIMEOUT);
    }, [INACTIVITY_TIMEOUT, handleInactivityTimeout]);

    // Set up inactivity tracking
    useEffect(() => {
        // Only track activity if user is authenticated
        if (!isAuthenticated) {
            return;
        }

        // Activity event handlers
        const activityEvents = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
        ];

        // Reset timer on any activity
        const handleActivity = () => {
            resetInactivityTimer();
        };

        // Add event listeners
        activityEvents.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });

        // Start initial timer
        resetInactivityTimer();

        // Cleanup function
        return () => {
            // Remove event listeners
            activityEvents.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });

            // Clear timer
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
        };
    }, [isAuthenticated, resetInactivityTimer]);

    // Initialize MSAL
    useEffect(() => {
        const initializeMsal = async () => {
            try {
                // Initialize MSAL instance
                await msalInstance.initialize();

                // Handle redirect promise
                const response = await msalInstance.handleRedirectPromise();

                // If we got a response, user just authenticated
                if (response) {
                    const userEmail = response.account.username || response.account.email || '';

                    setIsAuthenticated(true);
                    setUser({
                        name: response.account.name,
                        username: response.account.username,
                        email: userEmail,
                        localAccountId: response.account.localAccountId,
                        entraRoles: response.idTokenClaims?.roles || [],
                    });
                    return; // Don't check for existing accounts if we just authenticated
                }

                // Check if user is already signed in
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    setIsAuthenticated(true);
                    setUser({
                        name: accounts[0].name,
                        username: accounts[0].username,
                        email: accounts[0].username,
                        localAccountId: accounts[0].localAccountId,
                        entraRoles: [], // Will be populated from token claims
                    });
                }
            } catch (error) {
                console.error('MSAL initialization error:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        initializeMsal();

        // Listen for auth events
        const callbackId = msalInstance.addEventCallback((event) => {
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                const account = event.payload.account;
                setIsAuthenticated(true);
                setUser({
                    name: account.name,
                    username: account.username,
                    localAccountId: account.localAccountId,
                    entraRoles: event.payload.idTokenClaims?.roles || [],
                });
                setError(null);
            } else if (event.eventType === EventType.LOGIN_FAILURE) {
                setError(event.error?.message || 'Login failed');
            } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
                setIsAuthenticated(false);
                setUser(null);
                setError(null);
            }
        });

        return () => {
            if (callbackId) {
                msalInstance.removeEventCallback(callbackId);
            }
        };
    }, []);

    // Login function
    const login = async (loginHint = null) => {
        try {
            setError(null);
            const request = { ...loginRequest };

            if (loginHint) {
                request.loginHint = loginHint;
                // Add domain_hint for evoomedical.ro accounts to auto-select the account
                if (loginHint.includes('@evoomedical.ro')) {
                    request.domain_hint = 'evoomedical.ro';
                }
                // Force login prompt to use the specified loginHint instead of showing account picker
                request.prompt = 'login';
            }

            await msalInstance.loginRedirect(request);
        } catch (error) {
            console.error('Login error:', error);
            setError(error.message);
        }
    };

    // Get access token
    const getAccessToken = async () => {
        try {
            const account = msalInstance.getAllAccounts()[0];
            if (!account) {
                throw new Error('No account found');
            }

            const tokenResponse = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account,
            });

            return tokenResponse.accessToken;
        } catch (error) {
            console.error('Token acquisition error:', error);
            // Try interactive token acquisition
            try {
                const tokenResponse = await msalInstance.acquireTokenRedirect(loginRequest);
                return tokenResponse.accessToken;
            } catch (interactiveError) {
                throw interactiveError;
            }
        }
    };

    const value = {
        isAuthenticated,
        user,
        loading,
        error,
        login,
        logout,
        getAccessToken,
        msalInstance,
    };

    return (
        <EntraAuthContext.Provider value={value}>
            {children}
        </EntraAuthContext.Provider>
    );
};
